import { ChatCsat } from "@faire/help-center-self-serve/chat/views/ChatCsat";
import { getIsLoggedInUser } from "@faire/help-center-utils/serialized-data/getUser";
import { hasShortcutsAccess } from "@faire/help-center-utils/serialized-data/shortcutsAccess";
import { getSettingHelpCenterIssueSearch } from "@faire/help-center-utils/settings/getSettingHelpCenterIssueSearch";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { Route } from "@faire/web--source/ui/routing/Route";
import { Switch } from "@faire/web--source/ui/routing/Switch";
import {
  path as supportPath,
  route as supportRoute,
} from "@faire/web-api--source/routes/support";
import { route as supportSelfServeRoute } from "@faire/web-api--source/routes/support/self-serve";
import { route as supportSelfServeChatCsat } from "@faire/web-api--source/routes/support/self-serve/chat-csat";
import { route as supportSelfServeEmailRoute } from "@faire/web-api--source/routes/support/self-serve/email";
import { route as supportSelfServeIssueSearchRoute } from "@faire/web-api--source/routes/support/self-serve/issue-search";
import { route as supportSelfServeIssueSelectRoute } from "@faire/web-api--source/routes/support/self-serve/issue-select";
import { route as supportSelfServeOrderSelectRoute } from "@faire/web-api--source/routes/support/self-serve/order-select";
import { route as supportSelfServePhoneRoute } from "@faire/web-api--source/routes/support/self-serve/phone";
import { route as supportSelfServePhoneSubmissionConfirmationRoute } from "@faire/web-api--source/routes/support/self-serve/phone-submission-confirmation";
import { route as supportSelfServeRecommendedArticleRoute } from "@faire/web-api--source/routes/support/self-serve/recommended-article";
import { route as supportSelfServeEmailSubmissionConfirmationRoute } from "@faire/web-api--source/routes/support/self-serve/submission-confirmation";
import { route as supportSelfServeTopicsRoute } from "@faire/web-api--source/routes/support/self-serve/topics";
import React from "react";
import { Redirect } from "react-router-dom";

import { useNavigationInitializationEffect } from "./hooks/useNavigationInitializationEffect";
import { QueryState } from "./types";
import { Answer } from "./view/Answer/Answer";
import { SelfServeContentWrapper } from "./view/components/SelfServeContentWrapper";
import { EmailUsForm } from "./view/Email/EmailUsForm";
import { EmailSubmitted } from "./view/EmailSubmitted/EmailSubmitted";
import { Home } from "./view/Home/Home";
import { HomeWithShortcuts } from "./view/Home/HomeWithShortcuts";
import { IssueSearch } from "./view/IssueSearch/IssueSearch";
import { MoreIssue } from "./view/MoreIssue/MoreIssue";
import { Order } from "./view/Order/Order";
import { isPhoneInBusinessHours } from "./view/Phone/isPhoneInBusinessHours";
import { Phone } from "./view/Phone/Phone";
import { usePhoneAuthorization } from "./view/Phone/usePhoneAuthorization";
import { SelfServeSkeleton } from "./view/SelfServeSkeleton";
import { StepPage } from "./view/step";

export const SelfServeRouter: React.FC<QueryState> = ({ ...queryState }) => {
  const isLoggedInUser = getIsLoggedInUser();
  const { isMobile } = useViewport();
  const { hasPhoneAccess } = usePhoneAuthorization();

  useNavigationInitializationEffect();

  const renderStep = (stepPage: StepPage) => {
    let pageToRender;

    switch (stepPage) {
      case StepPage.TOPIC_SELECTION:
        pageToRender =
          !isMobile && hasShortcutsAccess() ? (
            <HomeWithShortcuts {...queryState} />
          ) : (
            <Home {...queryState} />
          );
        break;
      case StepPage.ORDER_SELECTION:
        pageToRender = <Order />;
        break;
      case StepPage.TELL_US_MORE:
        pageToRender = <SelfServeContentWrapper leftSection={<MoreIssue />} />;
        break;
      case StepPage.ANSWER:
        pageToRender = <SelfServeContentWrapper leftSection={<Answer />} />;
        break;
      case StepPage.EMAIL_FORM:
        pageToRender = (
          <SelfServeContentWrapper leftSection={<EmailUsForm />} />
        );
        break;
      case StepPage.PHONE_FORM:
        pageToRender = <SelfServeContentWrapper leftSection={<Phone />} />;
        break;
      case StepPage.EMAIL_SUBMISSION_CONFIRMATION:
        pageToRender = (
          <SelfServeContentWrapper
            leftSection={<EmailSubmitted supportType="email" />}
          />
        );
        break;
      case StepPage.PHONE_SUBMISSION_CONFIRMATION:
        pageToRender = (
          <SelfServeContentWrapper
            leftSection={<EmailSubmitted supportType="phone" />}
          />
        );
        break;
      case StepPage.ISSUE_SEARCH:
        pageToRender = <IssueSearch />;
        break;
      default:
        pageToRender = <Home {...queryState} />;
        break;
    }
    return <SelfServeSkeleton>{pageToRender}</SelfServeSkeleton>;
  };

  return (
    <Switch>
      {isLoggedInUser ? null : (
        <Redirect to={supportPath({ contact: "true" })} />
      )}
      <Route
        exact
        path={supportSelfServeRoute}
        render={() => renderStep(StepPage.TOPIC_SELECTION)}
      />
      <Route
        path={supportSelfServeTopicsRoute}
        render={() => renderStep(StepPage.TOPIC_SELECTION)}
      />
      <Route
        path={supportSelfServeOrderSelectRoute}
        render={() => renderStep(StepPage.ORDER_SELECTION)}
      />
      <Route
        path={supportSelfServeIssueSelectRoute}
        render={() => renderStep(StepPage.TELL_US_MORE)}
      />
      <Route
        path={supportSelfServeRecommendedArticleRoute}
        render={() => renderStep(StepPage.ANSWER)}
      />
      <Route
        path={supportSelfServeEmailRoute}
        render={() => renderStep(StepPage.EMAIL_FORM)}
      />
      <Route
        path={supportSelfServeEmailSubmissionConfirmationRoute}
        render={() => renderStep(StepPage.EMAIL_SUBMISSION_CONFIRMATION)}
      />
      <Route
        path={supportSelfServePhoneSubmissionConfirmationRoute}
        render={() => renderStep(StepPage.PHONE_SUBMISSION_CONFIRMATION)}
      />
      <Route path={supportSelfServeChatCsat} render={() => <ChatCsat />} />
      {hasPhoneAccess && isPhoneInBusinessHours() ? (
        <Route
          path={supportSelfServePhoneRoute}
          render={() => renderStep(StepPage.PHONE_FORM)}
        />
      ) : null}
      {getSettingHelpCenterIssueSearch() ? (
        <Route
          path={supportSelfServeIssueSearchRoute}
          render={() => renderStep(StepPage.ISSUE_SEARCH)}
        />
      ) : null}
      <Redirect to={supportRoute} />
    </Switch>
  );
};
